import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getDisplayStoreNumber } from '../../../utils/storeNumber';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CloudUploadOutlined,
  DeleteOutline,
  ImageOutlined,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import CustomBreadCrumbs from '../../../components/ui/CustomBreadCrumbs';
import { UserContext } from '../../../components/shared/useUser';
import useBannerAdmin from './hooks/useBannerAdmin';
import { BannerPreviewData } from './components/BannerPreview';
import DraggableBannerPreview from './components/DraggableBannerPreview';
import { BannerElementLayout, BannerElementStyles, CreateBannerRequest } from './types/BannerAdmin';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const BannerFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('banners');
  const theme = useTheme();
  const isEdit = Boolean(id);

  const { stores } = useContext(UserContext);

  const {
    getBanner,
    createBanner,
    updateBanner,
    uploadBannerImage,
    fetchSlots,
    slots,
  } = useBannerAdmin();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [tagText, setTagText] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaOpensNewTab, setCtaOpensNewTab] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imageAltText, setImageAltText] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#1A1A2E');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [isActive, setIsActive] = useState(true);
  const [elementLayout, setElementLayout] = useState<BannerElementLayout>({});
  const [elementStyles, setElementStyles] = useState<BannerElementStyles>({});
  const [scheduleStartUtc, setScheduleStartUtc] = useState('');
  const [scheduleEndUtc, setScheduleEndUtc] = useState('');
  const [assignmentType, setAssignmentType] = useState<'all' | 'stores'>('all');
  const [assignedStoreNumbers, setAssignedStoreNumbers] = useState<string[]>([]);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string>('');
  const [priority, setPriority] = useState<number>(1);

  /* ───── UI State ───── */
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    if (id) return;
    setElementStyles((prev) => ({
      ...prev,
      cta: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        ...prev.cta,
      },
    }));
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    getBanner(Number(id))
      .then((b) => {
        if (cancelled) return;
        setTitle(b.title);
        setSubtitle(b.subtitle ?? '');
        setTagText(b.tagText ?? '');
        setCtaText(b.ctaText ?? '');
        setCtaUrl(b.ctaUrl ?? '');
        setCtaOpensNewTab(b.ctaOpensNewTab);
        setImageUrl(b.imageUrl ?? '');
        setImagePreviewUrl(b.imageSasUrl ?? b.imageUrl ?? '');
        setImageAltText(b.imageAltText ?? '');
        setBackgroundColor(b.backgroundColor ?? '#1A1A2E');
        setTextColor(b.textColor ?? '#FFFFFF');
        setIsActive(b.isActive);
        setElementLayout(b.elementLayoutJson ? JSON.parse(b.elementLayoutJson) : {});
        setElementStyles(b.elementStylesJson ? JSON.parse(b.elementStylesJson) : {});
        setScheduleStartUtc(b.scheduleStartUtc ? b.scheduleStartUtc.slice(0, 16) : '');
        setScheduleEndUtc(b.scheduleEndUtc ? b.scheduleEndUtc.slice(0, 16) : '');
        setAssignmentType((b.assignmentType as 'all' | 'stores') ?? 'all');
        setAssignedStoreNumbers(b.assignedStoreNumbersJson ? JSON.parse(b.assignedStoreNumbersJson) : []);

        if (b.assignments?.length) {
          const active = b.assignments.find(a => a.isActive) ?? b.assignments[0];
          setSelectedSlotKey(active.slotKey);
          setPriority(active.priority);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSnackbar({ open: true, message: t('loadError'), severity: 'error' });
        }
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const previewData: BannerPreviewData = useMemo(() => ({
    title,
    subtitle,
    tag: tagText,
    ctaText,
    ctaUrl,
    imageUrl: imagePreviewUrl,
    bgColor: backgroundColor,
    textColor,
  }), [title, subtitle, tagText, ctaText, ctaUrl, imagePreviewUrl, backgroundColor, textColor]);

  const selectedSlot = useMemo(
    () => slots.find((s) => s.slotKey === selectedSlotKey),
    [slots, selectedSlotKey],
  );

  const processFile = useCallback(async (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: t('imageFormatError'), severity: 'error' });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setSnackbar({ open: true, message: t('imageSizeError'), severity: 'error' });
      return;
    }

    setIsUploading(true);
    try {
      const { imageUrl: uploadedUrl, previewUrl } = await uploadBannerImage(file);
      setImageUrl(uploadedUrl);
      setImagePreviewUrl(previewUrl);
      setSnackbar({ open: true, message: t('imageUploaded'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('imageUploadError'), severity: 'error' });
    } finally {
      setIsUploading(false);
    }
  }, [uploadBannerImage, t]);

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleRemoveImage = useCallback(() => {
    setImageUrl('');
    setImagePreviewUrl('');
    setImageAltText('');
  }, []);

  const handleSave = useCallback(async (publishActive: boolean) => {
    if (!selectedSlotKey) {
      return;
    }

    setIsSaving(true);
    const payload: CreateBannerRequest = {
      title,
      subtitle: subtitle || undefined,
      imageUrl: imageUrl || undefined,
      imageAltText: imageAltText || undefined,
      tagText: tagText || undefined,
      ctaText: ctaText || undefined,
      ctaUrl: ctaUrl || undefined,
      ctaOpensNewTab,
      backgroundColor: backgroundColor || undefined,
      textColor: textColor || undefined,
      isActive: publishActive,
      elementLayoutJson: Object.keys(elementLayout).length > 0 ? JSON.stringify(elementLayout) : undefined,
      elementStylesJson: Object.keys(elementStyles).length > 0 ? JSON.stringify(elementStyles) : undefined,
      scheduleStartUtc: scheduleStartUtc ? new Date(scheduleStartUtc).toISOString() : undefined,
      scheduleEndUtc: scheduleEndUtc ? new Date(scheduleEndUtc).toISOString() : undefined,
      assignmentType,
      assignedStoreNumbersJson: assignmentType === 'stores' && assignedStoreNumbers.length > 0
        ? JSON.stringify(assignedStoreNumbers)
        : undefined,
      slotKey: selectedSlotKey || undefined,
      priority: selectedSlotKey ? priority : undefined,
    };
    try {
      if (isEdit) {
        await updateBanner(Number(id), { ...payload, id: Number(id) });
        setSnackbar({ open: true, message: t('updateSuccess'), severity: 'success' });
      } else {
        await createBanner(payload);
        setSnackbar({ open: true, message: publishActive ? t('createSuccess') : t('draftSaved'), severity: 'success' });
      }

      setTimeout(() => navigate('/settings/content-management/banners'), 600);
    } catch {
      setSnackbar({ open: true, message: t('saveError'), severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [title, subtitle, imageUrl, imageAltText, tagText, ctaText, ctaUrl, ctaOpensNewTab, backgroundColor, textColor, elementLayout, elementStyles, scheduleStartUtc, scheduleEndUtc, assignmentType, assignedStoreNumbers, isEdit, id, updateBanner, createBanner, selectedSlotKey, priority, navigate, t]);

  const handleCancel = useCallback(() => {
    navigate('/settings/content-management/banners');
  }, [navigate]);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{isEdit ? t('editBanner') : t('createBanner')}</PageTitle>
        <Typography variant="textMD">{t('saving')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <CustomBreadCrumbs title={isEdit ? t('editBanner') : t('createBanner')}>
        <Link to="/settings/content-management/banners"
          component={RouterLink}>{t('bannerManagement')}</Link>
      </CustomBreadCrumbs>
      <PageTitle subTitle={isEdit ? t('editBannerSubtitle') : t('createBannerSubtitle')}>
        {isEdit ? t('editBanner') : t('createBanner')}
      </PageTitle>

      <Grid container
        spacing={8}
        sx={{ mt: 0 }}>

        <Grid item
          xs={12}
          lg={7}>
          <Stack spacing={6}>

            <Card elevation={0}
              sx={{ border: `1px solid ${theme.palette.custom.gray[200]}`, borderRadius: '12px' }}>
              <CardHeader
                title={
                  <Typography variant="textLG"
                    sx={{ fontWeight: 600 }}>
                    {t('contentSection')}
                  </Typography>
                }
                subheader={
                  <Typography variant="textSM"
                    sx={{ color: theme.palette.custom.gray[500], display: 'block', mt: 0.5 }}>
                    {t('contentDescription')}
                  </Typography>
                }
                sx={{ pb: 0, px: 6, pt: 5 }}
              />
              <CardContent sx={{ px: 6, pb: 6, pt: 6 }}>
                <Stack spacing={6}>
                  <TextField
                    label={t('title')}
                    placeholder={t('titlePlaceholder')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label={t('subtitle')}
                    placeholder={t('subtitlePlaceholder')}
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }}
                    spacing={4}>
                    <TextField
                      label={t('tagText')}
                      placeholder="Only This Week"
                      value={tagText}
                      onChange={(e) => setTagText(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }}
                    spacing={4}>
                    <TextField
                      label={t('ctaText')}
                      placeholder={t('ctaTextPlaceholder')}
                      value={ctaText}
                      onChange={(e) => setCtaText(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label={t('ctaUrl')}
                      placeholder={t('ctaUrlPlaceholder')}
                      helperText={t('ctaUrlHelperText')}
                      value={ctaUrl}
                      onChange={(e) => setCtaUrl(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={ctaOpensNewTab}
                        onChange={(e) => setCtaOpensNewTab(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="textSM">{t('ctaOpensNewTab')}</Typography>
                    }
                  />

                  <Box>
                    <Typography variant="textSM"
                      sx={{ fontWeight: 500, display: 'block' }}>
                      {t('layoutSection')} *
                    </Typography>
                    <Typography variant="textXS"
                      sx={{ color: theme.palette.custom.gray[500], display: 'block', mt: 0.5, mb: 2 }}>
                      {t('slotPickerDescription')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {slots.map((slot) => {
                        const isSelected = selectedSlotKey === slot.slotKey;
                        return (
                          <Box
                            key={slot.id}
                            onClick={() => setSelectedSlotKey(slot.slotKey)}
                            sx={{
                              flex: '1 1 0',
                              border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.custom.gray[200]}`,
                              borderRadius: '10px',
                              p: 3,
                              minHeight: 80,
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              backgroundColor: isSelected
                                ? alpha(theme.palette.primary.main, 0.04)
                                : 'transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="textSM"
                                sx={{ fontWeight: 600, display: 'block' }}>
                                {slot.slotName}
                              </Typography>
                              <Typography variant="textXS"
                                sx={{ color: theme.palette.custom.gray[400], fontWeight: 500 }}>
                                {slot.width} × {slot.height}
                              </Typography>
                            </Stack>
                            {slot.description && (
                              <Typography variant="textXS"
                                sx={{ color: theme.palette.custom.gray[500], display: 'block', mt: 0.5 }}>
                                {slot.description}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {selectedSlotKey && (
                    <TextField
                      label={t('priority')}
                      type="number"
                      value={priority}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(99, Number(e.target.value) || 1));
                        setPriority(val);
                      }}
                      inputProps={{ min: 1, max: 99 }}
                      helperText={t('priorityHelper')}
                      sx={{ maxWidth: 200 }}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0}
              sx={{ border: `1px solid ${theme.palette.custom.gray[200]}`, borderRadius: '12px' }}>
              <CardHeader
                title={
                  <Typography variant="textLG"
                    sx={{ fontWeight: 600 }}>
                    {t('imageUpload')}
                  </Typography>
                }
                subheader={
                  <Typography variant="textSM"
                    sx={{ color: theme.palette.custom.gray[500], display: 'block', mt: 0.5 }}>
                    {t('imageUploadDescription')}
                  </Typography>
                }
                sx={{ pb: 0, px: 6, pt: 5 }}
              />
              <CardContent sx={{ px: 6, pb: 6, pt: 6 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  hidden
                  aria-hidden
                  onChange={handleImageSelect}
                />

                {imagePreviewUrl ? (
                  <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={imagePreviewUrl}
                      alt={imageAltText || 'Banner image'}
                      sx={{
                        width: '100%',
                        maxHeight: 240,
                        objectFit: 'cover',
                        display: 'block',
                        borderRadius: '12px',
                      }}
                    />
                    <Stack direction="row"
                      spacing={1}
                      sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          backgroundColor: alpha(theme.palette.common.white, 0.9),
                          '&:hover': { backgroundColor: theme.palette.common.white },
                        }}
                      >
                        <CloudUploadOutlined fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleRemoveImage}
                        sx={{
                          backgroundColor: alpha(theme.palette.common.white, 0.9),
                          '&:hover': { backgroundColor: theme.palette.common.white },
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ) : (
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.custom.gray[300]}`,
                      borderRadius: '12px',
                      p: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      backgroundColor: isDragging
                        ? alpha(theme.palette.primary.main, 0.06)
                        : alpha(theme.palette.custom.gray[50], 0.5),
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <ImageOutlined sx={{ fontSize: 40, color: theme.palette.custom.gray[400] }} />
                    <Typography variant="textSM"
                      sx={{ fontWeight: 600, color: theme.palette.custom.gray[700] }}>
                      {isUploading ? t('uploadingImage') : t('dragDropImage')}
                    </Typography>
                    <Typography variant="textXS"
                      sx={{ color: theme.palette.custom.gray[500] }}>
                      {t('orClickToBrowse')}
                    </Typography>
                  </Box>
                )}

                {imageUrl && (
                  <TextField
                    label={t('imageAltText')}
                    value={imageAltText}
                    onChange={(e) => setImageAltText(e.target.value)}
                    fullWidth
                    sx={{ mt: 4 }}
                  />
                )}
              </CardContent>
            </Card>

            <Card elevation={0}
              sx={{ border: `1px solid ${theme.palette.custom.gray[200]}`, borderRadius: '12px' }}>
              <CardHeader
                title={
                  <Typography variant="textLG"
                    sx={{ fontWeight: 600 }}>
                    {t('scheduleSection')}
                  </Typography>
                }
                subheader={
                  <Typography variant="textSM"
                    sx={{ color: theme.palette.custom.gray[500], display: 'block', mt: 0.5 }}>
                    {t('scheduleDescription')}
                  </Typography>
                }
                sx={{ pb: 0, px: 6, pt: 5 }}
              />
              <CardContent sx={{ px: 6, pb: 6, pt: 6 }}>
                <Stack spacing={6}>
                  <Stack direction={{ xs: 'column', sm: 'row' }}
                    spacing={4}>
                    <TextField
                      label={t('scheduleStart')}
                      type="datetime-local"
                      value={scheduleStartUtc}
                      onChange={(e) => setScheduleStartUtc(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label={t('scheduleEnd')}
                      type="datetime-local"
                      value={scheduleEndUtc}
                      onChange={(e) => setScheduleEndUtc(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  <FormControl fullWidth>
                    <Typography variant="textSM"
                      sx={{ fontWeight: 500, mb: 1 }}>
                      {t('assignTo')}
                    </Typography>
                    <Select
                      value={assignmentType}
                      onChange={(e) => {
                        const val = e.target.value as 'all' | 'stores';
                        setAssignmentType(val);
                        if (val === 'all') setAssignedStoreNumbers([]);
                      }}
                      size="small"
                    >
                      <MenuItem value="all">{t('allStores')}</MenuItem>
                      <MenuItem value="stores">{t('specificStores')}</MenuItem>
                    </Select>
                  </FormControl>

                  {assignmentType === 'stores' && (
                    <Autocomplete
                      multiple
                      options={stores}
                      value={stores.filter(s => assignedStoreNumbers.includes(s.storeNumber ?? ''))}
                      getOptionLabel={(option) => `${option.storeName} (${getDisplayStoreNumber(option)})`}
                      isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                      onChange={(_e, newValue) => {
                        setAssignedStoreNumbers(newValue.map(s => s.storeNumber ?? '').filter(Boolean));
                      }}
                      disableCloseOnSelect
                      renderOption={(props, option, { selected }) => (
                        <li {...props}
                          key={option.storeNumber}>
                          <Checkbox checked={selected}
                            sx={{ mr: 1 }} />
                          <ListItemText
                            primary={option.storeName}
                            secondary={getDisplayStoreNumber(option)}
                          />
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((store, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={store.storeNumber}
                            label={`${store.storeName} (${store.storeNumber})`}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={assignedStoreNumbers.length === 0 ? t('searchStores') : ''}
                        />
                      )}
                      noOptionsText={t('noStoresFound')}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card elevation={0}
              sx={{ backgroundColor: 'transparent' }}>
              <CardContent sx={{ px: 0, py: 0, '&:last-child': { pb: 0 } }}>
                <Stack direction="row"
                  spacing={3}
                  justifyContent="flex-end">
                  <Button variant="secondary"
                    size="lg"
                    onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                  {!(isEdit && isActive) && (
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => handleSave(false)}
                      disabled={isSaving || !title.trim() || !selectedSlotKey}
                    >
                      {isSaving ? t('saving') : t('saveDraft')}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => handleSave(true)}
                    disabled={isSaving || !title.trim() || !imageUrl || !selectedSlotKey}
                  >
                    {isSaving ? t('saving') : isEdit && isActive ? t('update') : t('publish')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item
          xs={12}
          lg={5}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Typography variant="textLG"
                  sx={{ fontWeight: 600 }}>
                  {t('livePreview')}
                </Typography>
                <Typography variant="textSM"
                  sx={{ color: theme.palette.custom.gray[500] }}>
                  {t('livePreviewDescription')}
                </Typography>
              </Stack>

              <DraggableBannerPreview
                data={previewData}
                layout={elementLayout}
                onLayoutChange={setElementLayout}
                elementStyles={elementStyles}
                onElementStylesChange={setElementStyles}
                aspectRatio={selectedSlot
                  ? (selectedSlot.width > 0 && selectedSlot.height > 0 ? selectedSlot.width / selectedSlot.height : undefined)
                  : undefined}
              />
              {selectedSlot && (
                <Typography variant="textXS"
                  sx={{ color: theme.palette.custom.gray[500], mt: -2 }}>
                  {selectedSlot.slotName} • {selectedSlot.width} × {selectedSlot.height}
                </Typography>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbar.open}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        autoHideDuration={5000}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default BannerFormPage;